// ============================================================
// v8 — the standardized, portable "Pack" format (BOLT-007).
//
// A Pack bundles a play-area boundary + game/card settings + a deck
// of cards into one shareable artifact (file / paste / URL). It is
// PARTIAL by design — any of the three payloads may be omitted.
//
// A card's behaviour is a TAGGED UNION so the 28 built-ins ship now
// (`builtin` → an engine resolver by name + params) and community
// `script` cards (BOLT-009) slot in later with no format break.
//
// This module is the canonical home for the deck-config types
// (`DeckConfig`/`CardSetting`/`CardDeck`) — `cards.ts` re-exports
// them for back-compat, and `constants.ts` imports `DeckConfig`
// from here (breaks the old cards.ts↔constants.ts cycle). Pure
// types + pure helpers only: no value import of the catalog (the
// merge/sanitize helpers take the known ids as a parameter), so the
// format layer never depends on the deck it describes.
// ============================================================
import type { Boundary, CardFamily, CurseKind, HiderCardKind } from './protocol';
import type { Settings } from './constants';

export const PACK_SCHEMA_VERSION = 1 as const;

export type CardDeck = 'hider' | 'seeker';

/** Per-card enable/count override (Card Settings, v4). */
export interface CardSetting {
  enabled: boolean;
  count: number;
  /**
   * v29 BOLT-165 (additive, optional) — the host's COST override for a seeker
   * card: how many cards the hider is paid when it resolves.
   *
   * Jake: "I'd like the lobby's card settings to also be able to support
   * customizing the 'cost' of seeker cards."
   *
   * Absent ⇒ the catalog cost, which for the core deck is the auto-costed value
   * `costCard.ts` derived from the card's localization POWER (v8 BOLT-011).
   * Setting one is therefore a deliberate override of that model, not a gap in
   * it - which is why it is a separate field rather than a rewrite of the
   * catalog, and why an untouched room still runs on the derived numbers.
   */
  cost?: number;
}
/** Per-game overrides keyed by catalog id; absent ⇒ catalog default. */
export type DeckConfig = Record<string, CardSetting>;

/** Built-in resolvers a `builtin` card delegates to (the existing engine.ts). */
// v11 adds 'quadrants' (§2K) + 'borders' (§2L) — both resolve via cutGeometry.
export type BuiltinResolver = 'radar' | 'direction' | 'thermometer' | 'photoPenalty' | 'hider' | 'quadrants' | 'borders' | 'airhorn' | 'nearest'; // v24 BOLT-118 ('nearest' = the map-data cards, which take a TAG, not a number) // v16 BOLT-072 (no zone — resolves as a delivery, not geometry)

/** What the seeker can learn — bounds the validator's leakage check (BOLT-010). */
export type RevealClass = 'binary-disk' | 'wedge' | 'half-plane' | 'region-match' | 'blob' | 'none';

export type CardBehavior =
  // v24 BOLT-118: params may carry a string now — the landmark cards are
  // parameterised by an OSM TAG ('park', 'dining', …), not a distance.
  | { kind: 'builtin'; ref: BuiltinResolver; params?: Record<string, number | string> }
  | { kind: 'script'; lang: 'js'; source: string; revealClass: RevealClass };

/** Hider-card payload (for `behavior.ref === 'hider'`): bank / veto / curse. */
export interface HiderSpec {
  kind: HiderCardKind;
  bonusMin?: number;
  curse?: CurseKind;
}

/** The unified card definition — built-ins and community cards share this shape. */
export interface CardDef {
  id: string; // stable per card type (hider) or card (seeker)
  deck: CardDeck;
  label: string;
  description: string;
  defaultCount: number; // hider: copies in the deck | seeker: plays/round
  family?: CardFamily; // grouping + window behaviour (seeker)
  cost?: number; // seeker: hider cards dealt on resolution (auto-derivable, BOLT-011)
  behavior: CardBehavior;
  hider?: HiderSpec; // present when behavior.ref === 'hider'
  /** v16 BOLT-073: the card exists only in rooms whose capability floor
   *  (min declared protocolVersion) meets this bar. Absent = every room. */
  minProtocol?: number;
}

export interface PackManifest {
  schemaVersion: typeof PACK_SCHEMA_VERSION;
  id: string; // stable slug, e.g. "craig-ranch-north"
  name: string;
  author?: string;
  license?: string; // SPDX id or free text; required for the future registry
  description?: string;
  engineVersion?: string;
  boundary?: Boundary;
  settings?: Partial<Settings>;
  deck?: CardDef[];
}

// ---------------- merge (load a Pack into a game) ----------------

/** Deep-merge Pack settings onto a base (flat keys; `deckConfig` merged by id). */
/** v18 BOLT-081: the three retired per-card duration keys. A v6/v17 client (or
 *  a saved room, or a community Pack authored last version) may still send
 *  them; they are ACCEPTED and IGNORED for one version rather than rejected,
 *  because a settings error would break the room for a client that is
 *  otherwise perfectly able to play. */
const RETIRED_SETTING_KEYS = ['curseHoldupMs', 'curseSnailMs', 'curseBabelMs'] as const;

export function mergeSettings(base: Settings, override?: Partial<Settings>): Settings {
  if (!override) return base;
  const clean = { ...override } as Partial<Settings> & Record<string, unknown>;
  for (const k of RETIRED_SETTING_KEYS) delete clean[k];
  const out: Settings = { ...base, ...clean };
  if (override.deckConfig) out.deckConfig = { ...base.deckConfig, ...override.deckConfig };
  return out;
}

/** Merge a Pack's deck into a base deck: a colliding `id` OVERRIDES the
 *  built-in (in place), a new `id` is APPENDED. Order is otherwise preserved. */
export function mergeDeck(base: CardDef[], packDeck?: CardDef[]): CardDef[] {
  if (!packDeck || packDeck.length === 0) return base.slice();
  const byId = new Map(packDeck.map((c) => [c.id, c]));
  const out = base.map((c) => byId.get(c.id) ?? c); // override in place
  const baseIds = new Set(base.map((c) => c.id));
  for (const c of packDeck) if (!baseIds.has(c.id)) out.push(c); // add new
  return out;
}

// ---------------- sanitize (untrusted input → trusted, or null) ----------------

const MAX_LABEL = 80;
const MAX_DESC = 280;
const MAX_DECK = 200;
const MAX_SCRIPT_BYTES = 20_000;

function str(v: unknown, max: number): string | null {
  return typeof v === 'string' && v.length > 0 && v.length <= max ? v : null;
}
function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/** Validate + clamp a single CardDef from untrusted input; null ⇒ drop it. */
export function sanitizeCardDef(raw: unknown): CardDef | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const id = str(o.id, 64);
  const label = str(o.label, MAX_LABEL);
  const description = str(o.description, MAX_DESC) ?? '';
  if (!id || !label) return null;
  if (o.deck !== 'hider' && o.deck !== 'seeker') return null;
  const behavior = sanitizeBehavior(o.behavior);
  if (!behavior) return null;
  const def: CardDef = {
    id,
    deck: o.deck,
    label,
    description,
    defaultCount: Math.max(0, Math.min(50, Math.round(num(o.defaultCount) ?? 1))),
    behavior,
  };
  const cost = num(o.cost);
  if (cost !== null) def.cost = Math.max(0, Math.min(20, Math.round(cost)));
  if (typeof o.family === 'string') def.family = o.family as CardDef['family'];
  return def;
}

function sanitizeBehavior(raw: unknown): CardBehavior | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (o.kind === 'builtin') {
    const refs: BuiltinResolver[] = ['radar', 'direction', 'thermometer', 'photoPenalty', 'hider', 'quadrants', 'borders'];
    if (!refs.includes(o.ref as BuiltinResolver)) return null;
    const params: Record<string, number> = {};
    if (o.params && typeof o.params === 'object') {
      for (const [k, v] of Object.entries(o.params as Record<string, unknown>)) {
        const n = num(v);
        if (n !== null) params[k] = n;
      }
    }
    return { kind: 'builtin', ref: o.ref as BuiltinResolver, params };
  }
  if (o.kind === 'script') {
    const source = str(o.source, MAX_SCRIPT_BYTES);
    if (o.lang !== 'js' || !source) return null;
    const reveal: RevealClass[] = ['binary-disk', 'wedge', 'half-plane', 'region-match', 'blob', 'none'];
    const revealClass = (reveal.includes(o.revealClass as RevealClass) ? o.revealClass : 'blob') as RevealClass;
    return { kind: 'script', lang: 'js', source, revealClass };
  }
  return null;
}

export interface SanitizeResult {
  pack: PackManifest | null;
  error?: string;
}

/** Validate an untrusted Pack. Rejects an unsupported `schemaVersion` outright
 *  (never partially applies); drops malformed cards. */
export function sanitizePackManifest(raw: unknown): SanitizeResult {
  if (!raw || typeof raw !== 'object') return { pack: null, error: 'not an object' };
  const o = raw as Record<string, unknown>;
  if (o.schemaVersion !== PACK_SCHEMA_VERSION)
    return { pack: null, error: `unsupported schemaVersion ${String(o.schemaVersion)} (expected ${PACK_SCHEMA_VERSION})` };
  const id = str(o.id, 64);
  const name = str(o.name, MAX_LABEL);
  if (!id || !name) return { pack: null, error: 'missing id/name' };

  const pack: PackManifest = { schemaVersion: PACK_SCHEMA_VERSION, id, name };
  if (str(o.author, 80)) pack.author = o.author as string;
  if (str(o.license, 80)) pack.license = o.license as string;
  if (str(o.description, MAX_DESC)) pack.description = o.description as string;
  if (str(o.engineVersion, 32)) pack.engineVersion = o.engineVersion as string;

  if (o.boundary && typeof o.boundary === 'object') pack.boundary = o.boundary as Boundary; // shape re-checked at apply
  if (o.settings && typeof o.settings === 'object') pack.settings = o.settings as Partial<Settings>; // clamped by sanitizeDeckConfig + server
  if (Array.isArray(o.deck)) {
    pack.deck = o.deck.slice(0, MAX_DECK).map(sanitizeCardDef).filter((c): c is CardDef => c !== null);
  }
  return { pack };
}
